import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Header from "../../components/header";
import Navbar from "../../components/navbar";
import { DatePicker } from "antd";
import type { DatePickerProps } from "antd";
import type { Dayjs } from "dayjs";
import CalendarIcon from "../../components/calendarIcon";
const { RangePicker } = DatePicker;

export default function AIPlanner() {
  const [destination, setDestination] = useState("");
  // const [startDate, setStartDate] = useState("");
  // const [endDate, setEndDate] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  // const [showDateError, setShowDateError] = useState(false);
  const [selectedDates, setSelectedDates] = useState<[string, string]>([
    "",
    "",
  ]);
  const [isStartDateActive, setIsStartDateActive] = useState(false);
  const [isEndDateActive, setIsEndDateActive] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const defaultSuggestions = [
    { nation: "China", category: "Country" },
    { nation: "Indonesia", category: "Country" },
    { nation: "Japan", category: "Country" },
    { nation: "Singapore", category: "Country" },
  ];

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const navigate = useNavigate();

  const handleBlur = () => {
    setTimeout(() => {
      // 點擊建議項目後延遲隱藏
      setIsFocused(false);
      setShowSuggestions(false);
    }, 150);

    if (destination.trim() === "") {
      setShowError(true);
    } else {
      setShowError(false);
    }
  };
  // 使用 onCalendarChange 實現立即顯示日期的效果
  const handleDateChange = (dates: any, dateStrings: [string, string]) => {
    if (dates) {
      const [start, end] = dates;

      // 當選擇開始日期時
      if (start) {
        const formattedStart = start.format("MMM D, YYYY");
        setSelectedDates((prev) => [formattedStart, prev[1]]);
        // setStartDate(start.format("YYYY-MM-DD"));
      }
      // 當選擇結束日期時
      if (end) {
        const formattedEnd = end.format("MMM D, YYYY");
        setSelectedDates((prev) => [prev[0], formattedEnd]);
        // setEndDate(end.format("YYYY-MM-DD"));
      }

      // if (dates[0] && dates[1]) {
      //   setShowDateError(false);
      // }
    } else {
      // 當用戶清除日期選擇時
      setSelectedDates(["", ""]);
    }
  };

  const handleStartDateChange: DatePickerProps<Dayjs>["onChange"] = (date) => {
    if (date) {
      const formattedDate = date.format("MMM D, YYYY");
      setSelectedDates((prev) => [formattedDate, prev[1]]);
      // 檢查結束日期是否已選擇
      // if (selectedDates[1]) {
      //   setShowDateError(false);
      // }
    } else {
      setSelectedDates((prev) => ["", prev[1]]);
    }
  };
  const handleEndDateChange: DatePickerProps<Dayjs>["onChange"] = (date) => {
    if (date) {
      const formattedDate = date.format("MMM D, YYYY");
      setSelectedDates((prev) => [prev[0], formattedDate]);
      // 檢查開始日期是否已選擇
      // if (selectedDates[0]) {
      //   setShowDateError(false);
      // }
    } else {
      setSelectedDates((prev) => [prev[0], ""]);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError(""); // 清除之前的錯誤信息

    // 驗證目的地
    if (destination.trim() === "") {
      setShowError(true);
      return;
    }

    // // 驗證日期
    // if (
    //   (selectedDates[0] && !selectedDates[1]) ||
    //   (!selectedDates[0] && selectedDates[1]) ||
    //   (!selectedDates[0] && !selectedDates[1]) ||
    //   selectedDates[0] === selectedDates[1]
    // ) {
    //   setShowDateError(true);
    //   return;
    // }
    // setShowDateError(false);

    setIsLoading(true); // 開始加載
    try {
      // 格式化日期
      const formattedDate =
        selectedDates[0] && selectedDates[1]
          ? `${selectedDates[0].toLowerCase()}–${selectedDates[1].toLowerCase()}`
          : "";

      // console.log("formattedDate:", formattedDate);

      // 準備請求數據
      const requestData = {
        destination: destination.trim(),
        travelDate: formattedDate,
      };

      // 發送API請求
      const response = await fetch(
        "https://exploreasiait-h5-backend-750219299255.asia-northeast1.run.app/api/ai-planner",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        throw new Error("API request failed.");
      }

      const data = await response.json();
      // console.log("API response data:", data);

      // 跳轉到結果頁面
      navigate("/ai-planner/test-answer", { state: { planData: data } });
    } catch (error) {
      console.error("API request error:", error);
      setApiError(
        error instanceof Error
          ? error.message
          : "Request failed, please try again later."
      );
    } finally {
      setIsLoading(false); // 結束加載
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>AI Travel Planner - Create Your Perfect Asian Journey</title>
        <meta
          name="description"
          content="Plan your perfect Asian trip with our AI-powered travel planner. Get personalized itineraries, local insights, and travel recommendations for your dream destination."
        />
      </Helmet>
      <Header />
      <Navbar />
      <div className="w-full flex justify-center">
        <form
          autoComplete="off"
          onSubmit={handleSubmit}
          className="flex flex-col items-center w-full max-w-[666px] min-w-[400px] px-8 py-12"
        >
          <h1 className="text-3xl font-bold mb-8">Plan a new trip</h1>
          <div className="relative w-full bg-yellow-0">
            <div
              className="w-full h-full px-4 absolute flex items-center cursor-text bg-red-0"
              onClick={() =>
                document.getElementById("destination-input")?.focus()
              }
            >
              <div
                className={`absolute left-4 transition-all duration-150 ease-in-out bg-green-0 ${
                  destination || isFocused
                    ? "top-2 text-[10px]"
                    : "top-[50%] -translate-y-1/2"
                }`}
              >
                <span className="text-[#212529] font-extrabold">Where to?</span>
                <i
                  className={`font-medium text-[#adb5bd] ml-2 transition-opacity duration-0 ${
                    destination || isFocused ? "opacity-0" : "opacity-100"
                  }`}
                >
                  e.g. China, Indonesia, Japan
                </i>
              </div>
            </div>
            <input
              id="destination-input"
              type="text"
              value={destination}
              onChange={(e) => {
                const value = e.target.value;
                setDestination(value);

                if (value.trim() !== "") {
                  setShowError(false);
                }
                setShowSuggestions(true);
              }}
              onFocus={() => {
                setIsFocused(true);
                setShowSuggestions(true);
              }}
              onBlur={handleBlur}
              placeholder={isFocused ? "e.g. China, Indonesia, Japan" : ""}
              className={`w-full h-14 pt-7 px-4 pb-4 border-[1px] border-[#dee2e6] outline-none rounded-lg placeholder:text-[#adb5bd] font-medium text-[#495057] ${
                isFocused ? "ai-boxShadow border-[#aeb6f3]" : ""
              }`}
            />
            {/* 顯示建議選項 */}
            {showSuggestions && destination.trim() !== "" && (
              <ul className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-md">
                {defaultSuggestions
                  .filter((item) =>
                    item.nation
                      .toLowerCase()
                      .includes(destination.toLowerCase())
                  )
                  .map((suggestion) => (
                    <li
                      key={suggestion.nation}
                      className="flex justify-between px-4 py-4 text-sm font-semibold cursor-pointer hover:bg-[#ebebeb]"
                      onClick={() => {
                        setDestination(suggestion.nation);
                        setShowSuggestions(false);
                        setShowError(false);
                      }}
                    >
                      <span className="text-[#495057]">
                        {suggestion.nation}
                      </span>
                      <span className="text-xs text-[#1e2125]">
                        {suggestion.category}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
          {showError && (
            <span className="text-[10px] text-red-600 mt-1.5">
              Choose a destination to start planning
            </span>
          )}
          <div className="w-full relative flex-grow mt-6">
            <div className="w-full relative rounded-lg flex items-center">
              <div className="w-full h-full flex items-center absolute px-4 text-[#6c757]">
                <div className="absolute text-[10px] text-[#212529] text-left font-extrabold pl-[1px] pt-2 top-0 left-4 right-4 translate-y-0 transition-all duration-200 ease-in-out">
                  <span>Dates (optional)</span>
                </div>
              </div>
              <div className="relative border-[1px] border-[dee2e6] rounded-lg w-full h-14 px-4 pt-[25px] pb-4 text-left text-[#495057] block text-sm font-medium transition-shadow duration-150 ease-in-out">
                <div className="flex flex-row">
                  <button
                    type="button"
                    className="w-[120px] p-0 bg-yellow-0 flex flex-shrink-0 items-center border-0 bg-transparent text-[#6c757d] mr-1 sm:mr-2"
                  >
                    <div className="w-full flex items-center p-0">
                      <div className="w-5 h-5 mr-1.5">
                        <CalendarIcon isActive={isStartDateActive} />
                      </div>
                      <span>{selectedDates[0] || "Start date"}</span>
                    </div>
                  </button>
                  <div className="w-[1px] bg-[#dee2e6] mx-3 my-1"></div>
                  <button
                    type="button"
                    className="w-[120px] p-0 bg-yellow-0 flex flex-shrink-0 items-center border-0 bg-transparent text-[#6c757d] flex-grow"
                  >
                    <div className="w-full flex items-center p-0">
                      <div className="w-5 h-5 mr-1.5">
                        <CalendarIcon isActive={isEndDateActive} />
                      </div>
                      <span>{selectedDates[1] || "End date"}</span>
                    </div>
                  </button>
                </div>
                {windowWidth > 650 ? (
                  <div className="absolute bottom-0 left-4 opacity-0 cursor-pointer">
                    <RangePicker
                      size="large"
                      onCalendarChange={handleDateChange}
                      style={{ width: "104%" }}
                      onOpenChange={(open) => {
                        if (!open) {
                          setIsStartDateActive(false);
                          setIsEndDateActive(false);
                        }
                      }}
                      onFocus={(_, info) => {
                        if ("source" in info && info.source === "submit")
                          return;
                        if (info.range === "start") {
                          setIsStartDateActive(true);
                          setIsEndDateActive(false);
                        } else if (info.range === "end") {
                          setIsStartDateActive(false);
                          setIsEndDateActive(true);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="absolute bottom-0 opacity-0 flex gap-5">
                    <DatePicker
                      onChange={handleStartDateChange}
                      showNow={false}
                      placeholder="Start date"
                      style={{ width: "130px" }}
                      onFocus={() => setIsStartDateActive(true)}
                      onBlur={() => setIsStartDateActive(false)}
                    />
                    <DatePicker
                      onChange={handleEndDateChange}
                      showNow={false}
                      placeholder="End date"
                      style={{ width: "138px" }}
                      onFocus={() => setIsEndDateActive(true)}
                      onBlur={() => setIsEndDateActive(false)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* {showDateError && (
            <span className="text-[10px] text-red-600 mt-1.5">
              Please select a complete date range
            </span>
          )} */}

          {apiError && (
            <span className="text-sm text-red-600 mt-4">{apiError}</span>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="px-8 py-4 mt-12 bg-[#5b68d3]  text-white font-extrabold rounded-full hover:bg-[#4752c6] disabled:bg-gray-400 disabled:cursor-not-allowed relative"
          >
            {isLoading ? (
              <>
                <span className="opacity-30">Start planning</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </>
            ) : (
              "Start planning"
            )}
          </button>
        </form>
      </div>
    </>
  );
}
